import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { CustomAttributes, Identifiers } from '@atlaskit/feature-gate-js-client/types';

import { initConfigClientFromValues } from './config';
import { featureGatesOptions } from './options';

export interface TownsquareStatsigBootstrap {
	readonly clientSdkKey?: string;
	readonly experimentValues?: Record<string, unknown>;
	readonly identifiers?: Identifiers;
	readonly customAttributes?: CustomAttributes;
}

interface TownsquareStatsigConfigBootstrap {
	readonly configCollectionValues?: unknown;
}

const STATSIG_BOOTSTRAP_SCRIPT_ID = 'townsquare-statsig-bootstrap';
const STATSIG_CONFIG_BOOTSTRAP_SCRIPT_ID = 'townsquare-statsig-config-bootstrap';

let parsedStatsigBootstrap: TownsquareStatsigBootstrap | undefined;
let hasReadStatsigBootstrap = false;
let parsedStatsigConfigBootstrap: TownsquareStatsigConfigBootstrap | undefined;
let hasReadStatsigConfigBootstrap = false;
let bootstrapInitialisationPromise: Promise<void> | null = null;
let configInitialisedFromBootstrap = false;

function getErrorMessage(error: unknown): string {
	return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

function readBootstrapJson<T>(scriptId: string, label: string): T | undefined {
	const script = document.getElementById(scriptId);
	if (!script?.textContent) {
		return undefined;
	}

	try {
		return JSON.parse(script.textContent) as T;
	} catch (error) {
		console.warn(`[statsig-bootstrap] failed to parse ${label} JSON (${getErrorMessage(error)})`);
		return undefined;
	}
}

export function readTownsquareStatsigBootstrap(): TownsquareStatsigBootstrap | undefined {
	if (hasReadStatsigBootstrap) {
		return parsedStatsigBootstrap;
	}

	hasReadStatsigBootstrap = true;
	parsedStatsigBootstrap = readBootstrapJson<TownsquareStatsigBootstrap>(
		STATSIG_BOOTSTRAP_SCRIPT_ID,
		'FeatureGates bootstrap',
	);

	return parsedStatsigBootstrap;
}

function readTownsquareStatsigConfigBootstrap(): TownsquareStatsigConfigBootstrap | undefined {
	if (hasReadStatsigConfigBootstrap) {
		return parsedStatsigConfigBootstrap;
	}

	hasReadStatsigConfigBootstrap = true;
	parsedStatsigConfigBootstrap = readBootstrapJson<TownsquareStatsigConfigBootstrap>(
		STATSIG_CONFIG_BOOTSTRAP_SCRIPT_ID,
		'config bootstrap',
	);

	return parsedStatsigConfigBootstrap;
}

export async function initialiseFeatureGatesFromBootstrapOnce(): Promise<boolean> {
	const statsigBootstrap = readTownsquareStatsigBootstrap();

	if (
		!statsigBootstrap?.clientSdkKey ||
		!statsigBootstrap.experimentValues ||
		!statsigBootstrap.identifiers ||
		!statsigBootstrap.customAttributes
	) {
		return false;
	}

	if (bootstrapInitialisationPromise) {
		try {
			await bootstrapInitialisationPromise;
			return true;
		} catch (error) {
			console.warn(
				`[statsig-bootstrap] FeatureGates bootstrap initialisation failed (${getErrorMessage(
					error,
				)})`,
			);
			return false;
		}
	}

	bootstrapInitialisationPromise = FeatureGates.initializeFromValues(
		{
			...featureGatesOptions,
			sdkKey: statsigBootstrap.clientSdkKey,
		},
		statsigBootstrap.identifiers,
		statsigBootstrap.customAttributes,
		statsigBootstrap.experimentValues,
	).catch((error) => {
		bootstrapInitialisationPromise = null;
		throw error;
	});

	try {
		await bootstrapInitialisationPromise;
		return true;
	} catch (error) {
		console.warn(
			`[statsig-bootstrap] FeatureGates bootstrap initialisation failed (${getErrorMessage(
				error,
			)})`,
		);
		return false;
	}
}

export function initialiseConfigFromBootstrapOnce(): boolean {
	if (configInitialisedFromBootstrap) {
		return true;
	}

	const configCollectionValues = readTownsquareStatsigConfigBootstrap()?.configCollectionValues;
	if (
		!configCollectionValues ||
		(typeof configCollectionValues !== 'string' && typeof configCollectionValues !== 'object')
	) {
		console.warn('[statsig-bootstrap] config bootstrap unavailable', {
			hasBootstrap: Boolean(readTownsquareStatsigBootstrap()),
			hasConfigCollectionValues: Boolean(configCollectionValues),
			configCollectionValuesType: typeof configCollectionValues,
		});
		return false;
	}

	try {
		initConfigClientFromValues(configCollectionValues as string | Record<string, unknown>);
		configInitialisedFromBootstrap = true;
		return true;
	} catch (error) {
		console.warn(
			`[statsig-bootstrap] config bootstrap initialisation failed (${getErrorMessage(error)})`,
		);
		return false;
	}
}
